import { Head } from '@inertiajs/react';
import { useState, useEffect, useRef } from 'react';
import { Link, router } from '@inertiajs/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Search, Users, Stethoscope, Sparkles, Heart, Shield, GraduationCap,
  CheckCircle2, ArrowRight
} from 'lucide-react';
import FilterSearchAutocomplete from '../components/FilterSearchAutocomplete';

// Scroll-triggered fade-in using IntersectionObserver
const AnimateIn = ({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
    >
      {children}
    </div>
  );
};

export default function LandingPage() {
  const [location, setLocation] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);

  const categoryMap: Record<string, string> = {
    'SPECIALTIES': 'specialties',
    'BODY_ZONES': 'body_zones',
    'ACCESSIBILITY': 'accessibility',
    'EQUIPMENT': 'equipment',
    'LANGUAGES': 'languages',
    'TECHNIQUES': 'techniques',
    'PRACTICE_LOCATIONS': 'practice_locations',
    'PATIENT_TYPES': 'patient_types',
    'PAYMENT_METHODS': 'payment_methods',
    'APPOINTMENT_METHODS': 'appointment_methods'
  };

  const handleFilterSelect = (suggestion: { id: number; value: string; category_key: string }) => {
    if (!selectedFilters.find(f => f.id === suggestion.id)) {
      setSelectedFilters([...selectedFilters, suggestion]);
    }
  };

  const handleFilterRemove = (suggestion: { id: number; value: string; category_key: string }) => {
    setSelectedFilters(selectedFilters.filter(f => f.id !== suggestion.id));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const searchParams: any = {
      city: location || undefined,
    };

    selectedFilters.forEach(filter => {
      const filterKey = categoryMap[filter.category_key];
      if (filterKey) {
        if (!searchParams[filterKey]) {
          searchParams[filterKey] = [];
        }
        if (!searchParams[filterKey].includes(filter.value)) {
          searchParams[filterKey].push(filter.value);
        }
      }
    });

    router.get('/search', searchParams);
  };

  return (
    <>
      <Head title="Ethos Santé – Trouvez le kiné adapté à vos besoins" />

      <div className="min-h-screen flex flex-col">
        <main className="flex-grow">

          {/* ── Hero Section ── */}
          <section className="relative overflow-hidden bg-gradient-to-br from-[#015963] via-[#008788] to-[#31953a]">
            {/* Decorative blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-[#f0d104]/10 blur-3xl" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-20 md:pb-32">
              <div className="md:flex md:items-center md:gap-16">
                {/* Text */}
                <div className="md:w-1/2 mb-10 md:mb-0 text-center md:text-left">
                  <AnimateIn>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
                      Trouvez le kiné qui correspond à vos besoins&nbsp;!
                    </h1>
                  </AnimateIn>
                  <AnimateIn delay={100}>
                    <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-lg mx-auto md:mx-0">
                      Ethos Santé vous aide à trouver le professionnel adapté à votre problématique.
                    </p>
                  </AnimateIn>
                </div>

                {/* Image */}
                <AnimateIn delay={150} className="md:w-1/2">
                  <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20">
                    <img
                      src="/images/hero.png"
                      alt="Illustration Ethos Santé - activités physiques et bien-être"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </AnimateIn>
              </div>

              {/* Search bar – glass card */}
              <AnimateIn delay={300}>
                <form onSubmit={handleSearch} className="relative z-20 mt-12 max-w-5xl mx-auto">
                  <div className="backdrop-blur-xl bg-white/95 rounded-2xl shadow-2xl p-5 sm:p-6 ring-1 ring-black/5">
                    <div className="flex flex-col md:flex-row gap-3 items-start">
                      <div className="flex-1 min-w-0 w-full md:w-auto">
                        <FilterSearchAutocomplete
                          placeholder="Spécialité, zone du corps, équipement..."
                          onSelect={handleFilterSelect}
                          onRemove={handleFilterRemove}
                          selectedFilters={selectedFilters}
                          className="w-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0 w-full md:w-auto self-start">
                        <Input
                          name="location"
                          placeholder="Ville ou code postal"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className="w-full h-10"
                        />
                      </div>
                      <div className="flex gap-2 w-full md:w-auto self-start">
                        <Button
                          type="submit"
                          className="bg-[#008788] hover:bg-[#015963] text-white px-8 h-10 flex-1 md:flex-none whitespace-nowrap rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          Rechercher
                        </Button>
                        <Link
                          href="/search"
                          className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                          Filtres
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </AnimateIn>

            </div>
          </section>

          {/* ── Features Section ── */}
          <section className="py-20 sm:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <AnimateIn>
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    Une plateforme complète pour patients et professionnels
                  </h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Ethos Santé répond aux besoins spécifiques des patients et des kinésithérapeutes
                    pour une meilleure expérience de soins.
                  </p>
                </div>
              </AnimateIn>

              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <Users className="h-6 w-6" />,
                    title: "Patients",
                    description: "Trouvez le praticien idéal, répondant à vos besoins spécifiques, et dont les compétences sont vérifiées.",
                    gradient: "from-[#008788] to-[#00bcbe]"
                  },
                  {
                    icon: <Stethoscope className="h-6 w-6" />,
                    title: "Kinésithérapeutes",
                    description: "Mettez en valeur vos compétences spécifiques et recevez des patients correspondant à votre expertise.",
                    gradient: "from-[#31953a] to-[#73ab11]"
                  },
                  {
                    icon: <Sparkles className="h-6 w-6" />,
                    title: "Ethos Santé",
                    description: "Notre mission : améliorer la qualité des soins par une meilleure correspondance entre patients et praticiens.",
                    gradient: "from-[#eec600] to-[#f6d709]"
                  }
                ].map((feature, i) => (
                  <AnimateIn key={feature.title} delay={i * 120}>
                    <div className="group relative bg-white rounded-2xl border border-gray-100 p-8 h-full flex flex-col text-center shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      <div className={`mx-auto mb-5 flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg`}>
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-500 leading-relaxed flex-grow">{feature.description}</p>
                    </div>
                  </AnimateIn>
                ))}
              </div>
            </div>
          </section>

          {/* ── Benefits Section ── */}
          <section className="py-20 sm:py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="md:flex md:items-start md:gap-16">
                {/* Image */}
                <AnimateIn className="md:w-5/12 mb-12 md:mb-0 md:sticky md:top-24">
                  <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
                    <img
                      src="/images/logo-complet.jpg"
                      alt="Illustration représentant les valeurs d'Ethos Santé"
                      className="w-full h-auto"
                    />
                  </div>
                </AnimateIn>

                {/* Content */}
                <div className="md:w-7/12 space-y-14">
                  {/* Patients */}
                  <AnimateIn>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                        Patients :
                      </h2>
                      <div className="space-y-4">
                        {[
                          "Recherchez le praticien qui vous correspond et selon vos critères.",
                          "Comprenez les différentes techniques et outils utilisés grâce à notre blog d'informations.",
                          "Contactez facilement votre kiné par le moyen qu'il aura sélectionné.",
                          "Trouvez un kiné approuvé par Ethos qui aura répondu à une évaluation de ses compétences."
                        ].map((text, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-[#31953a] mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600 leading-relaxed">{text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AnimateIn>

                  {/* Kinés */}
                  <AnimateIn>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                        Kinés&nbsp;: Mettez en valeur vos compétences&nbsp;!
                      </h2>
                      <div className="space-y-4">
                        {[
                          "Vous avez fait une formation complémentaire, investi dans du matériel, faites-le savoir !",
                          "Valorisez vraiment votre savoir-faire, répondez aux certifications proposées par Ethos.",
                          "Gagnez du temps en sélectionnant un moyen de contact privilégié, en indiquant que vous ne prenez plus de nouveaux patients ou en précisant un type de patientèle."
                        ].map((text, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-[#008788] mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600 leading-relaxed">{text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AnimateIn>

                  {/* Médecins */}
                  <AnimateIn>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                        Médecins/Prescripteurs :
                      </h2>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#eec600] mt-0.5 flex-shrink-0" />
                          <p className="text-gray-600 leading-relaxed">Aidez vos patients à trouver un kiné adapté à sa pathologie.</p>
                        </div>
                      </div>
                    </div>
                  </AnimateIn>
                </div>
              </div>
            </div>
          </section>

          {/* ── Values Section ── */}
          <section className="py-20 sm:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <AnimateIn>
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    Nos valeurs
                  </h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Chez Ethos Santé, nous nous engageons à améliorer l'expérience des soins de kinésithérapie.
                  </p>
                </div>
              </AnimateIn>

              <div className="grid md:grid-cols-3 gap-10">
                {[
                  {
                    icon: <Heart className="h-7 w-7" />,
                    title: "Caractère / Empathie",
                    description: "Parce qu'un bon professionnel de santé sera aussi celui qui sera à l'écoute de vos besoins et de vos attentes.",
                    color: "text-rose-500",
                    bg: "bg-rose-50"
                  },
                  {
                    icon: <Shield className="h-7 w-7" />,
                    title: "Valeurs / Moralité / Éthique",
                    description: "Ethos Santé veut promouvoir des soins de qualité, la facilité d'accès aux informations pour les patients et ainsi limiter le phénomène d'errance thérapeutique.",
                    color: "text-[#008788]",
                    bg: "bg-[#008788]/10"
                  },
                  {
                    icon: <GraduationCap className="h-7 w-7" />,
                    title: "Compétences / Expertise",
                    description: "Ethos Santé met en valeur les compétences et l'expertise des kinés référencés : des kinésithérapeutes formés et continuant à se former régulièrement, avec des formations de qualité.",
                    color: "text-[#31953a]",
                    bg: "bg-[#31953a]/10"
                  }
                ].map((value, i) => (
                  <AnimateIn key={value.title} delay={i * 120}>
                    <div className="text-center">
                      <div className={`${value.bg} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${value.color}`}>
                        {value.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                      <p className="text-gray-500 leading-relaxed">{value.description}</p>
                    </div>
                  </AnimateIn>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA Section ── */}
          <section className="relative overflow-hidden py-20 sm:py-24 bg-gradient-to-br from-[#015963] via-[#008788] to-[#31953a]">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-1/4 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute bottom-0 left-1/4 w-60 h-60 rounded-full bg-[#f0d104]/10 blur-3xl" />
            </div>

            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <AnimateIn>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Prêt·e à trouver votre kiné idéal ?
                </h2>
                <p className="text-lg sm:text-xl text-white/75 mb-10 max-w-2xl mx-auto">
                  Rejoignez Ethos Santé dès aujourd'hui et bénéficiez d'une kinésithérapie adaptée à vos besoins.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link
                    href="/search"
                    className="inline-flex justify-center items-center px-8 py-4 text-lg font-semibold rounded-xl text-[#015963] bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    Je cherche un kiné
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    href="/pour-les-kines"
                    className="inline-flex justify-center items-center px-8 py-4 text-lg font-semibold rounded-xl text-white border-2 border-white/30 hover:bg-white hover:text-[#015963] transition-all duration-200"
                  >
                    Je suis kiné
                  </Link>
                </div>
              </AnimateIn>
            </div>
          </section>

        </main>
      </div>
    </>
  );
}
